import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Rating
} from "@mui/material";
import {
  Search,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment-timezone";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";

//Services
import ratingsApi from "Services/Ratings";

const Driverratings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  dayjs.extend(relativeTime);
  dayjs.extend(customParseFormat);

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  //Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(1);
  const [searchText, setSearchText] = useState("");
  const timezone = useSelector((state) => state.timezone.timezone);

  const getDriverratings = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalRecords(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, search: "" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    ratingsApi.getDriverratingsList(params).then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id, dispatch]);

  const getAllDriverratings = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = (err) => {
      console.error("error fetch bulk", err);
    };
    const params = { page: 1, limit: "unlimited" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    ratingsApi.getDriverratingsList(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  useEffect(() => {
    getDriverratings();
    getAllDriverratings();
  }, [getDriverratings, getAllDriverratings]);

  function formatDate(inputDate) {
    if (!inputDate) return "--";
    const dateValue = inputDate?.$date || inputDate;
    if (!dateValue) return "--";
    const tz = timezone || "UTC";
    if (!moment(dateValue).isValid()) return "--";
    return moment(dateValue).tz(tz).format("YYYY-MM-DD hh:mm A");
  }

  const calculateSno = (pageIndex, pageSize, index) => {
    return (pageIndex - 1) * pageSize + index + 1;
  };

  const filteredRow = (row || []).filter((data) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const passengerName = (data?.userdetails?.user_name || "").toLowerCase();
    const driverName = (data?.driverdetails?.user_name || "").toLowerCase();
    return (
      (data?.bookingId || "").toLowerCase().includes(searchLower) ||
      passengerName.includes(searchLower) ||
      driverName.includes(searchLower) ||
      (data?.comments || "").toLowerCase().includes(searchLower) ||
      `${data?.rating || ""}`.includes(searchLower)
    );
  });

  const dataRows = _.map(filteredRow || [], (data, index) => ({
    sno: calculateSno(page, pageSize, index),
    bookingId: data?.bookingId,
    passengername: data?.userdetails?.user_name,
    drivername: data?.driverdetails?.user_name,
    rating: data?.rating,
    dateandtime: formatDate(data?.createdAt),
    relativeTime: dayjs(formatDate(data?.createdAt), "YYYY-MM-DD hh:mm A").isValid()
      ? dayjs(formatDate(data?.createdAt), "YYYY-MM-DD hh:mm A").fromNow()
      : "Invalid date",
    comments: data?.comments === "" ? "No Comments" : data?.comments,
    row: data,
  }));

  const bulkdataRows = _.map(
    _.orderBy(bulkrow || [], ["createdAt"], ["asc"]),
    (data, index) => ({
      sno: index + 1,
      bookingId: data?.bookingId,
      passengername: data?.userdetails?.user_name,
      drivername: data?.driverdetails?.user_name,
      rating: data?.rating,
      dateandtime: formatDate(data?.createdAt),
      comments: data?.comments === "" ? "No Comments" : data?.comments,
      row: data,
    })
  );

  const handleDownloadAll = () => {
    const bulkformatteddatas = bulkdataRows.map((row) => ({
      "S.NO": row.sno,
      "Booking ID": row.bookingId,
      "Passenger name": row.passengername,
      "Driver name": row.drivername,
      "Rating": row.rating,
      "Date & time": row.dateandtime,
      "Comments": row.comments,
    }));
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All Driverratings.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    const columnWidths = [5, 15, 25, 25, 10, 20, 30];
    const padText = (text, width) => (text ?? "").toString().padEnd(width, " ");
    const header = [
      padText("S.NO", columnWidths[0]),
      padText("Booking ID", columnWidths[1]),
      padText("Passenger name", columnWidths[2]),
      padText("Driver name", columnWidths[3]),
      padText("Rating", columnWidths[4]),
      padText("Date & time", columnWidths[5]),
      padText("Comments", columnWidths[6]),
    ].join("");

    const rows = dataRows
      .map((row) =>
        [
          padText(row.sno, columnWidths[0]),
          padText(row.bookingId, columnWidths[1]),
          padText(row.passengername, columnWidths[2]),
          padText(row.drivername, columnWidths[3]),
          padText(row.rating, columnWidths[4]),
          padText(row.dateandtime, columnWidths[5]),
          padText(row.comments, columnWidths[6]),
        ].join("")
      )
      .join("\n");

    const formattedContent = [header, rows].join("\n");
    navigator.clipboard.writeText(formattedContent).then(() => {
      dispatch(
        showSnackbar({
          message: "Copied table to clipboard!",
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        })
      );
    });
  };

  const formatteddatas = dataRows.map((row) => ({
    "S.NO": row.sno,
    "Booking ID": row.bookingId,
    "Passenger name": row.passengername,
    "Driver name": row.drivername,
    "Rating": row.rating,
    "Date & time": row.dateandtime,
    "Comments": row.comments,
  }));

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Driverratings");
    XLSX.writeFile(workbook, "Driverratings.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Driverratings.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((row) => [
      row.sno,
      row.bookingId,
      row.passengername,
      row.drivername,
      row.rating,
      row.dateandtime,
      row.comments,
    ]);
    doc.autoTable({
      head: [
        [
          "S.NO",
          "Booking ID",
          "Passenger name",
          "Driver name",
          "Rating",
          "Date & time",
          "Comments",
        ],
      ],
      body: tableData,
    });
    doc.save("Driverratings.pdf");
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };

  // Pagination Logic
  const totalPagesCount = Math.ceil(totalRecords / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1; // 0-indexed for calculation

    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) {
        pages.push(i);
      }
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [
          0,
          "...",
          totalPagesCount - 3,
          totalPagesCount - 2,
          totalPagesCount - 1,
        ];
      } else {
        pages = [
          0,
          "...",
          internalPage - 1,
          internalPage,
          internalPage + 1,
          "...",
          totalPagesCount - 1,
        ];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }

      return (
        <Button
          key={item}
          className={
            internalPage === item
              ? classes.pageButtonActive
              : classes.pageButton
          }
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header Container */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Driver Ratings</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Driver Ratings</span>
        </Typography>
      </Box>

      {/* Actions and Search Bar Row */}
      <Box className={classes.actionRow}>
        <Box className={classes.searchWrapper}>
          <Search className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search"
            value={searchText}
            onChange={handleSearchChange}
            className={classes.searchInput}
          />
        </Box>

        <Box style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          {/* Download/Copy bar */}
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={handleCopy}>
              Copy
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadExcel}>
              Excel
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadCSV}>
              CSV
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadPDF}>
              PDF
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadAll}>
              Download All
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 0.5 }}>
                S.No
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Booking Id
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>
                Passenger Name
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>
                Driver Name
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1 }}>
                Rating
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>
                Date & Time
              </TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>
                Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell} style={{ flex: 0.5 }}>
                  {rowItem.sno}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`} style={{ flex: 1 }}>
                  {rowItem.bookingId}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1.5 }}>
                  {rowItem.passengername}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1.5 }}>
                  {rowItem.drivername}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1 }}>
                  <Rating
                    name="read-only"
                    value={Number(rowItem.rating) || 0}
                    readOnly
                    precision={0.5}
                    size="small"
                  />
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1.5 }}>
                  {rowItem.relativeTime}
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1.5 }}>
                  {rowItem.comments}
                </TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "32px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No records found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, totalRecords)} of {totalRecords} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />

            {renderPagination()}

            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default Driverratings;
